import { TabContent } from './TabContent';
import * as React from 'react';
import { Tabs, useTheme } from '@material-ui/core';
import { TabPanel, TabPanelProps } from './TabPanel';
import { BasicTab } from './BasicTab';
import clsx from 'clsx';
import { capitalize } from 'lodash';
import { TabsWrapper } from './TabsWrapper';

export interface BasicTabsProps {
  tabs: Array<TabContent>;
  selectedTabIndex?: number;
  tabsId: string;
  tabsPanelId: string;
  textColor?: 'secondary' | 'primary' | 'inherit';
  scrollButtons?: 'auto' | 'desktop' | 'on' | 'off';
  indicatorColor?: 'secondary' | 'primary';
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  selectionFollowsFocus?: boolean;
  centered?: boolean;
  orientation?: 'horizontal' | 'vertical';
  ariaLabel?: string;
  size?: 'small' | 'medium';
  scrollableContent?: boolean;
  className?: string;
  handleTabChange?: (tab: TabContent) => void;
  tabPanels?: Array<React.ReactElement<TabPanelProps>>;
}

export function BasicTabs(props: BasicTabsProps) {
  const {
    selectedTabIndex,
    tabs,
    tabsId,
    tabsPanelId,
    textColor,
    scrollButtons,
    indicatorColor,
    variant,
    selectionFollowsFocus,
    centered,
    orientation,
    ariaLabel,
    size = 'medium',
    className,
    scrollableContent,
    handleTabChange,
    tabPanels,
  } = props;
  const theme = useTheme();
  const [tabIndex, setTabIndex] = React.useState<number>(selectedTabIndex ?? 0);
  // used for case when state is managed otside this component
  React.useEffect(() => {
    if (!!handleTabChange) {
      setTabIndex(selectedTabIndex ?? 0);
    }
  }, [handleTabChange, selectedTabIndex]);

  const handleSelectedTabIndexChange = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    //when state is managed outside the index is come from the parrent component
    if (!!handleTabChange) {
      handleTabChange(tabs[newValue]);
    } else {
      setTabIndex(newValue);
    }
  };
  const getTabProps = React.useCallback(
    (item: TabContent) => {
      return {
        id: `${tabsId}-${item.Id}`,
        'aria-controls': `${tabsPanelId}-${item.Id}`,
        label: item.Name,
      };
    },
    [tabsId, tabsPanelId],
  );
  return (
    <TabsWrapper
      theme={theme}
      className={clsx('tabsWrapper', {
        tabsVertical: orientation === 'vertical',
        tabsHorizontal: orientation !== 'vertical',
        scrollContent: scrollableContent === true,
      })}
    >
      <Tabs
        className={clsx(className, 'basicTabs', `size${capitalize(size)}`)}
        indicatorColor={indicatorColor || 'primary'}
        textColor={textColor || 'inherit'}
        orientation={orientation || 'horizontal'}
        value={tabIndex}
        onChange={handleSelectedTabIndexChange}
        variant={variant || 'fullWidth'}
        selectionFollowsFocus={selectionFollowsFocus || true}
        centered={centered}
        scrollButtons={scrollButtons}
        aria-label={ariaLabel || 'basic tabs'}
      >
        {tabs.map(item => (
          <BasicTab
            className={'basicTab'}
            {...getTabProps(item)}
            startIcon={item.startIcon}
            endIcon={item.endIcon}
            mainIcon={item.icon}
            size={size}
            key={`tab_${item.Id}`}
            //fullWidth={false}
            //wrapped={false}
          />
        ))}
      </Tabs>
      {!!tabPanels && tabPanels.length > 0 ? (
        <React.Fragment>{tabPanels}</React.Fragment>
      ) : (
        <React.Fragment>
          {tabs.map((item, index) => (
            <TabPanel
              value={tabIndex}
              index={index}
              tabsId={tabsId}
              tabsPanelId={tabsPanelId}
              scrollableContent={scrollableContent}
              key={`tabpanel_${item.Id}`}
            >
              {item.Content}
            </TabPanel>
          ))}
        </React.Fragment>
      )}
    </TabsWrapper>
  );
}
